// Pure, DOM-free REST client for the owner portal. No Svelte, no browser globals
// beyond an injectable `fetch` — so it's unit-tested in the node vitest project.
// The portal is served from the SAME origin as the API, so the default base is ""
// (relative URLs). The owner's login JWT is sent as a Bearer on every call.

export * from "./api-types";
import type {
  OrgSummary, OrgDetail, OrgConfigDef, VendorGroup, OrgGroups,
  OnboardInput, OnboardResult, User, Me, SearchEvent, PageSnapshot, ReleaseFeed,
  ActivateReleaseResult, Entitlement, Prospect, ProspectPage, ProspectPatch, ProspectQuery,
} from "./api-types";

export type FetchLike = typeof fetch;

/** Thrown on a non-2xx response, carrying the server's {error} message + status. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface Api {
  readonly token: string | null;
  setToken(t: string | null): void;
  login(email: string, password: string): Promise<{ token: string; user: User }>;
  /** The signed-in account (identity + role). 401 if the token is invalid. */
  me(): Promise<Me>;
  listOrgs(): Promise<OrgSummary[]>;
  createOrg(name: string): Promise<{ id: string }>;
  orgDetail(id: string): Promise<OrgDetail>;
  /**
   * Every org's OWN supplier definitions — the real, per-org configs. There is no
   * shared vendor list to read, so this is the only supplier source the portal uses.
   */
  orgConfigDefs(): Promise<OrgConfigDef[]>;
  renameOrg(id: string, name: string): Promise<void>;
  /** Set the org's plan and/or trial end (null clears it). Returns the resolved entitlement. */
  setOrgPlan(
    id: string,
    patch: { plan?: "trial" | "paid"; trialEndsAt?: string | null },
  ): Promise<Entitlement>;
  deleteOrg(id: string): Promise<void>;
  addMember(orgId: string, email: string, role: string): Promise<void>;
  /** Change an existing member's role (member/admin/owner). */
  setMemberRole(orgId: string, userId: string, role: string): Promise<void>;
  removeMember(orgId: string, userId: string): Promise<void>;
  setOrgConfig(orgId: string, configId: string, enabled: boolean): Promise<void>;
  /** This org's vendor groups + the set's CAS version. */
  orgGroups(orgId: string): Promise<OrgGroups>;
  /** Full-replace save carrying the version it was edited FROM; a stale version 409s. */
  saveOrgGroups(orgId: string, groups: VendorGroup[], version: number): Promise<OrgGroups>;
  /** Atomically create an org + its admin user (invited when no password is given). */
  onboardOrg(input: OnboardInput): Promise<OnboardResult>;
  listUsers(): Promise<User[]>;
  /** Create a login account. With a password it's immediate; without, an invite is emailed. */
  createUser(email: string, role: "portal" | "app", password?: string): Promise<void>;
  /** Flip a user between 'portal' (console admin) and 'app' (org/customer). */
  setUserRole(email: string, role: "portal" | "app"): Promise<void>;
  inviteUser(email: string): Promise<void>;
  resetUserPassword(email: string, password: string): Promise<void>;
  deleteUser(email: string): Promise<void>;
  listSearches(opts?: { limit?: number; after?: string; orgId?: string }): Promise<SearchEvent[]>;
  /** Captured page snapshots (newest first). List rows omit the stored HTML. */
  listSnapshots(opts?: { vendorId?: string; limit?: number; orgId?: string }): Promise<PageSnapshot[]>;
  /** One snapshot INCLUDING its stored HTML (for rendering in the viewer). */
  getSnapshot(id: string): Promise<PageSnapshot>;
  /** The desktop update feed: versions on the volume + the active version per platform. */
  releases(): Promise<ReleaseFeed>;
  /**
   * Offer a version to customers: regenerate the latest*.yml manifests and record the
   * promotion. Also the rollback path. A staged build is offered by NOTHING else.
   */
  activateRelease(version: string, opts?: { note?: string }): Promise<ActivateReleaseResult>;
  listProspects(q?: ProspectQuery): Promise<ProspectPage>;
  updateProspect(cui: string, patch: ProspectPatch): Promise<Prospect>;
  exportProspectsCsv(q?: ProspectQuery): Promise<string>;
}

/** Build a portal API client. `fetchImpl` is injectable for tests. */
export function createApi(baseUrl = "", token: string | null = null, fetchImpl?: FetchLike): Api {
  const doFetch: FetchLike = fetchImpl ?? ((...a) => fetch(...a));
  let tok = token;

  async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    if (tok) headers.Authorization = `Bearer ${tok}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const res = await doFetch(`${baseUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    let json: unknown = null;
    try {
      json = await res.json();
    } catch {
      /* empty/204 body */
    }
    if (!res.ok) {
      const msg =
        json && typeof json === "object" && "error" in json
          ? String((json as { error: unknown }).error)
          : `HTTP ${res.status}`;
      throw new ApiError(res.status, msg);
    }
    return json as T;
  }

  function qs(o: Record<string, string | number | undefined>): string {
    const p = Object.entries(o)
      .filter(([, v]) => v !== undefined && v !== "")
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
    return p.length ? `?${p.join("&")}` : "";
  }

  return {
    get token() {
      return tok;
    },
    setToken(t) {
      tok = t;
    },
    async login(email, password) {
      const r = await req<{ token: string; user: User }>("POST", "/auth/login", { email, password });
      tok = r.token;
      return r;
    },
    me() {
      return req<Me>("GET", "/auth/me");
    },
    async listOrgs() {
      return (await req<{ orgs: OrgSummary[] }>("GET", "/orgs")).orgs;
    },
    async createOrg(name) {
      return (await req<{ org: { id: string } }>("POST", "/orgs", { name })).org;
    },
    orgDetail(id) {
      return req<OrgDetail>("GET", `/orgs/${encodeURIComponent(id)}`);
    },
    async orgConfigDefs() {
      return (await req<{ defs: OrgConfigDef[] }>("GET", "/org-config-defs")).defs;
    },
    async renameOrg(id, name) {
      await req("PATCH", `/orgs/${encodeURIComponent(id)}`, { name });
    },
    async setOrgPlan(id, patch) {
      const r = await req<{ entitlement: Entitlement }>(
        "PATCH",
        `/orgs/${encodeURIComponent(id)}`,
        patch,
      );
      return r.entitlement;
    },
    async deleteOrg(id) {
      await req("DELETE", `/orgs/${encodeURIComponent(id)}`);
    },
    async addMember(orgId, email, role) {
      await req("POST", `/orgs/${encodeURIComponent(orgId)}/members`, { email, role });
    },
    async setMemberRole(orgId, userId, role) {
      await req(
        "PATCH",
        `/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`,
        { role },
      );
    },
    async removeMember(orgId, userId) {
      await req("DELETE", `/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`);
    },
    async setOrgConfig(orgId, configId, enabled) {
      await req("PUT", `/orgs/${encodeURIComponent(orgId)}/configs/${encodeURIComponent(configId)}`, {
        enabled,
      });
    },
    async orgGroups(orgId) {
      const r = await req<{ groups?: VendorGroup[]; version?: number }>(
        "GET",
        `/orgs/${encodeURIComponent(orgId)}/groups`,
      );
      return { groups: r.groups ?? [], version: r.version ?? 0 };
    },
    async saveOrgGroups(orgId, groups, version) {
      const r = await req<{ groups?: VendorGroup[]; version?: number }>(
        "PUT",
        `/orgs/${encodeURIComponent(orgId)}/groups`,
        { groups, version },
      );
      return { groups: r.groups ?? [], version: r.version ?? 0 };
    },
    onboardOrg(input) {
      return req<OnboardResult>("POST", "/orgs/onboard", input);
    },
    async listUsers() {
      return (await req<{ users: User[] }>("GET", "/users")).users;
    },
    async createUser(email, role, password) {
      await req("POST", "/users", password ? { email, role, password } : { email, role });
    },
    async setUserRole(email, role) {
      await req("PATCH", `/users/${encodeURIComponent(email)}/role`, { role });
    },
    async inviteUser(email) {
      await req("POST", `/users/${encodeURIComponent(email)}/invite`);
    },
    async resetUserPassword(email, password) {
      await req("PATCH", `/users/${encodeURIComponent(email)}`, { password });
    },
    async deleteUser(email) {
      await req("DELETE", `/users/${encodeURIComponent(email)}`);
    },
    async listSearches(opts = {}) {
      return (await req<{ events: SearchEvent[] }>("GET", `/searches${qs(opts)}`)).events;
    },
    async listSnapshots(opts = {}) {
      return (
        await req<{ snapshots: PageSnapshot[] }>("GET", `/page-snapshots${qs(opts)}`)
      ).snapshots;
    },
    getSnapshot(id) {
      return req<{ snapshot: PageSnapshot }>(
        "GET",
        `/page-snapshots/${encodeURIComponent(id)}`,
      ).then((r) => r.snapshot);
    },
    releases() {
      return req<ReleaseFeed>("GET", "/desktop-releases");
    },
    activateRelease(version, opts) {
      return req<ActivateReleaseResult>("POST", "/desktop-releases/activate", {
        version,
        note: opts?.note,
      });
    },
    listProspects(q = {}) {
      return req<ProspectPage>("GET", `/admin/prospects${qs({ ...q })}`);
    },
    async updateProspect(cui, patch) {
      return (await req<{ prospect: Prospect }>("PATCH", `/admin/prospects/${encodeURIComponent(cui)}`, patch)).prospect;
    },
    async exportProspectsCsv(q = {}) {
      const res = await doFetch(`${baseUrl}/admin/prospects/export.csv${qs({ ...q, page: undefined, pageSize: undefined })}`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`);
      return res.text();
    },
  };
}
